import { useRTL } from "themes/RTLProvider/RTLProvider";
import { useTranslation } from "_metronic/i18n/language";
import { ApiQuestionType } from "app/types";
import { useEffect, useState } from "react";
import { getScore, ScoreResultType } from "utils/getScore";
import { calculateTimeDifference } from "utils";
import {
  ParamsI,
  ResultQuizCognitiveType,
} from "../CognitiveResults/cognitive-result-view";
import { Box, Button, Card, Title, Header } from "app/pages/admin/components";
import Modal from "app/pages/admin/components/Modal";
import Paragraph from "app/pages/admin/components/Paragraph";
import { Chip } from "@mui/material";
import { Spinner } from "react-bootstrap";
import ModalResultPDF from "./ModalResultPDF";

type ModalCognitiveDetailsProps = {
  quizAnswer: ResultQuizCognitiveType;
  openModal: boolean;
  setOpenModal: (isOpen: boolean) => void;
  findQuestion: (questionId: number) => Promise<ApiQuestionType>;
  loading: boolean;
  setLoading: (e: boolean) => void;
  handleAnswerPress: (value: any) => any;
  accountId: ParamsI | undefined;
  accountSelected: any;
  quizName?: string;
};
const ModalCognitiveDetails = ({
  quizAnswer,
  openModal,
  setOpenModal,
  findQuestion,
  setLoading,
  loading,
  handleAnswerPress,
  accountId,
  accountSelected,
  quizName,
}: ModalCognitiveDetailsProps) => {
  const isRtl = useRTL();
  const [totalScore, setTotalScore] = useState<number>(0);
  const [openPDF, setOpenPDF] = useState(false);
  const [dataScore, setDataScore] = useState<ScoreResultType[]>([]);
  const translate = useTranslation();
  const answersWithScores = { ...quizAnswer, dataScore };

  const handleOpenPDF = (isOpen: boolean) => {
    setOpenPDF(isOpen);
  };

  useEffect(() => {
    const getData = async () => {
      setTotalScore(0);
      let total = 0;

      if (quizAnswer.results && quizAnswer.results.length > 0) {
        for (const answer of quizAnswer.results) {
          const question: any = await findQuestion(answer.questionId);
          const score =
            getScore(question, answer, answer.categoryCognitiveQuestionId)
              ?.score || 0;

          if (
            answer.categoryCognitiveQuestionId === 2 &&
            answer.questionId === 4
          ) {
            continue;
          }

          total += score;
        }
      }

      setTotalScore(total);
    };

    getData();
    loadDataScore();
  }, [quizAnswer]);

  const initDate = new Date(quizAnswer.results[1]?.initDate);
  const endDate = new Date(
    quizAnswer.results[quizAnswer.results.length - 1]?.endDateTime,
  );
  const timeTotal =
    initDate && endDate ? calculateTimeDifference(initDate, endDate) : "N/A";

  const loadDataScore = async () => {
    const scores: ScoreResultType[] = [];
    await Promise.all(
      quizAnswer.results.map(async (answer, index) => {
        const question: any = await findQuestion(answer.questionId);
        const score = getScore(
          question,
          answer,
          answer.categoryCognitiveQuestionId,
        );

        scores[index] = score;
      }),
    );
    setDataScore(scores);
  };

  return !loading ? (
    <>
      <Modal
        setShowModal={setOpenModal}
        showModal={openModal}
        style={{
          width: "80%",
          padding: "0",
          maxHeight: "600px",
          marginTop: "50px",
        }}
      >
        <Card style={styles.container}>
          <Card.Header>
            <Header>
              {accountSelected.name} - ID: {quizAnswer.testResultId}
            </Header>
          </Card.Header>
          <Card.Title>
            <Title style={styles.title}>
              {translate("screenApp.results.answer.title")}
              {quizName ? `: ${quizName}` : ""}
            </Title>
          </Card.Title>
          <Card.Body style={{ padding: "20px" }}>
            <Box style={styles.totalScore}>
              <Paragraph style={{ width: "auto" }}>
                <Paragraph.Bold>
                  {translate("screenApp.results.answer.score.title")}:
                </Paragraph.Bold>{" "}
                {totalScore !== 0 && totalScore}
              </Paragraph>
              {totalScore === 0 && (
                <Spinner style={{ borderWidth: "4px", marginLeft: ".5em" }} />
              )}
            </Box>

            {quizAnswer.results.some(
              answer => answer.typeQuiz === "screening",
            ) && (
              <>
                <Title style={styles.subtitle}>
                  {translate("screenApp.results.answer.screening")}:
                </Title>
                {quizAnswer.results
                  .filter(answer => answer.typeQuiz === "screening")
                  .map(answer => (
                    <DisplayAnswer
                      key={answer.id}
                      answer={answer}
                      findQuestion={findQuestion}
                    />
                  ))}
              </>
            )}

            {quizAnswer.results.some(
              answer => answer.typeQuiz === "anamnese",
            ) && (
              <>
                <Title style={styles.subtitle}>
                  {translate("screenApp.results.answer.initials")}:
                </Title>
                {quizAnswer.results
                  .filter(answer => answer.typeQuiz === "anamnese")
                  .map(answer => (
                    <DisplayAnswer
                      key={answer.id}
                      answer={answer}
                      findQuestion={findQuestion}
                    />
                  ))}
              </>
            )}

            {quizAnswer.results.some(answer => answer.typeQuiz === "quiz") && (
              <>
                <Title style={styles.subtitle}>
                  {translate("screenApp.results.answer.main")}:
                </Title>
                {quizAnswer.results
                  .filter(answer => answer.typeQuiz === "quiz")
                  .map(answer => (
                    <DisplayAnswer
                      key={answer.id}
                      answer={answer}
                      findQuestion={findQuestion}
                      pressable
                      handleAnswerPress={handleAnswerPress}
                      time={true}
                    />
                  ))}
              </>
            )}

            <Paragraph
              style={{
                fontSize: "20px",
                marginBottom: "16px",
                textAlign: "left",
                fontWeight: "bold",
                borderColor: "#ccc",
                padding: "4px 10px",
                borderRadius: "5px",
              }}
            >
              {translate("screenApp.results.answer.total.time")}: {timeTotal}
            </Paragraph>
          </Card.Body>

          <Card.Actions
            style={{
              justifyContent: "center",
              flexDirection: "row",
              display: "flex",
            }}
          >
            {totalScore ? (
              <Button
                style={styles.buttom}
                onClick={() => {
                  handleOpenPDF(true);
                }}
              >
                {translate("screenApp.results.openPdf").toUpperCase()}
              </Button>
            ) : (
              <Spinner style={{ borderWidth: "4px", color: "#007bff" }} />
            )}
          </Card.Actions>
        </Card>
      </Modal>
      <ModalResultPDF
        openPDF={openPDF}
        setOpenPDF={handleOpenPDF}
        data={answersWithScores}
        accountId={accountId}
        account={accountSelected}
        totalScore={totalScore}
        // dataScore={dataScore}
        // setData={setItemData}
        // accountId={account?.id}
        // itIsAnamnesis={itIsAnamnese}
      />
    </>
  ) : (
    <></>
  );
};

type DisplayAnswerProps = {
  answer: any;
  pressable?: boolean;
  time?: boolean;
  handleAnswerPress?: (value: any) => any;
  findQuestion: (questionId: number) => Promise<ApiQuestionType>;
};
const DisplayAnswer = ({
  answer,
  pressable = false,
  time = false,
  findQuestion,
  handleAnswerPress,
}: DisplayAnswerProps) => {
  const [question, setQuestion] = useState({} as ApiQuestionType);
  const translate = useTranslation();

  useEffect(() => {
    const start = async () => {
      const data = await findQuestion(answer.questionId);
      setQuestion(data);
    };
    start();
  }, []);

  const timeDiff = calculateTimeDifference(
    new Date(answer.initDate),
    new Date(answer.endDateTime),
  );

  let dataScore: ScoreResultType;
  if (question) {
    dataScore = getScore(question, answer, answer.categoryCognitiveQuestionId);
  }

  const renderStyledChip = (label: string) => (
    <Chip
      label={label}
      variant="outlined"
      sx={{
        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
        fontSize: "1.2rem",
        height: "38px",
        minWidth: "150px",
      }}
    />
  );

  const renderChipScore = () => {
    const score = dataScore?.score ?? 0;

    if (answer.categoryCognitiveQuestionId === 2 && answer.questionId === 4) {
      return renderStyledChip(
        translate("screenApp.results.answer.does.not.score"),
      );
    }

    if (score !== null && score >= 0) {
      return renderStyledChip(`${translate("labels.score")}: ${score}`);
    }

    return renderStyledChip("Erro");
  };

  const renderChipIntrusion = () => {
    const intrusions = dataScore?.intrusions ?? 0;

    if (intrusions > 0) {
      return (
        <Chip
          variant="outlined"
          label={`${translate("labels.intrusions")}: ${intrusions}`}
          sx={{
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            fontSize: "1.2rem",
            height: "38px",
            minWidth: "150px",
            marginLeft: "10px",
          }}
        />
      );
    }
  };

  return (
    answer.endDateTime && (
      <Card key={answer.id} style={styles.answerContainer}>
        <Card.Title>
          {" "}
          {`${translate("screenApp.results.question")}: ${answer.question}`}
        </Card.Title>
        <Card.Body>
          {answer.rightAnswer && (
            <Paragraph style={styles.answer}>
              <Paragraph.Bold>
                {translate("screenApp_results_answer_true")}:
              </Paragraph.Bold>{" "}
              {answer.rightAnswer}
            </Paragraph>
          )}
          <Paragraph style={styles.answer}>
            <Paragraph.Bold>
              {translate("screens_quiz_labels_answer")}:
            </Paragraph.Bold>{" "}
            {answer.answer}
          </Paragraph>
          <Paragraph style={styles.date}>
            <Paragraph.Bold>{translate("labels_home")}:</Paragraph.Bold>{" "}
            {new Date(answer.initDate).toLocaleString()}
          </Paragraph>
          <Paragraph style={styles.date}>
            <Paragraph.Bold> {translate("labels_end")}:</Paragraph.Bold>{" "}
            {new Date(answer.endDateTime).toLocaleString()}
          </Paragraph>
        </Card.Body>

        {pressable && handleAnswerPress && (
          <Card.Actions
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px",
            }}
          >
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "start",
                gap: "10px",
                padding: "0",
              }}
            >
              {renderChipScore()}
              {renderChipIntrusion()}

              {time && <Paragraph>{timeDiff}</Paragraph>}
            </Box>
            <Button
              color="info"
              style={{ fontSize: "1.1rem" }}
              onClick={() => handleAnswerPress(answer)}
            >
              {translate("screenApp_listPacient_buttonsPacient_buttonDetail")}
            </Button>
          </Card.Actions>
        )}
      </Card>
    )
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    padding: "0",
    margin: "0",
  },
  title: {
    fontSize: "34px",
    marginBottom: "16px",
    textAlign: "center",
  },
  totalScore: {
    fontSize: "20px",
    marginBottom: "16px",
    fontWeight: "bold",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#ccc",
    padding: "4px 10px",
    borderRadius: "5px",

    justifyContent: "center",
  },
  subtitle: {
    fontWeight: "bold",
    textAlign: "start",
    fontSize: "20px",
    margin: "16px 0",
  },
  timetotal: {
    fontSize: "20px",
    margin: "16px 0",
    textAlign: "right",
  },
  answerContainer: {
    marginBottom: "20px",
    borderRadius: "8px",
    paddingBottom: "8px",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
  },
  answerTextContainer: {
    flex: 1,
  },
  question: {
    fontSize: "18px",
    fontWeight: "bold",
  },
  answer: {
    fontSize: "16px",
    margin: "5px 0",
    fontWeight: "bold",
  },
  date: {
    fontSize: "14px",
    color: "gray",
  },
  timeDifference: {
    fontSize: "14px",
    fontWeight: "bold",
    textAlign: "left",
    alignSelf: "center",
  },
  button: {
    backgroundColor: "#007bff",
    color: "#fff",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "bold",
    border: "none",
    cursor: "pointer",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
    transition: "all 0.3s ease",
    outline: "none",
  },
  bottomView: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
};

export default ModalCognitiveDetails;

// modal feita em material/UI

// import { Close } from "@mui/icons-material";
// import {
//   Box,
//   Button,
//   CardContent,
//   CardHeader,
//   Chip,
//   IconButton,
//   Modal,
// } from "@mui/material";
// import { Card } from "reactstrap";
// import { useRTL } from "themes/RTLProvider/RTLProvider";
// import { useTranslation } from "_metronic/i18n/language";
// import { ApiQuestionType } from "app/types";
// import { useEffect, useState } from "react";
// import { getScore, ScoreResultType } from "utils/getScore";
// import { calculateTimeDifference } from "utils";
// import {
//   ParamsI,
//   ResultQuizCognitive,
// } from "../CognitiveResults/cognitive-result-view";
// import ModalResultPDF from "./ModalResultPDF";

// type ModalCognitiveDetailsProps = {
//   quizAnswer: ResultQuizCognitive;
//   openModal: boolean;
//   setOpenModal: (isOpen: boolean) => void;
//   findQuestion: (questionId: number) => Promise<ApiQuestionType>;
//   loading: boolean;
//   setLoading: (e: boolean) => void;
//   accountId: ParamsI | undefined;
//   accountSelected: any
// };
// const ModalCognitiveDetails = ({
//   quizAnswer,
//   openModal,
//   setOpenModal,
//   findQuestion,
//   setLoading,
//   accountSelected,
//   loading,
//   accountId,
// }: ModalCognitiveDetailsProps) => {
//   const isRtl = useRTL();
//   const valueTransformIfRtl = isRtl ? "scaleX(-1)" : undefined;
//   const [totalScore, setTotalScore] = useState<number>(0);
//   const [openPDF, setOpenPDF] = useState(false);
//   const [dataScore, setDataScore] = useState<ScoreResultType[]>([]);
//   const translate = useTranslation();

//   const handleClose = () => {
//     setOpenModal(false);
//   };

//   const handleOpenPDF = (isOpen: boolean) => {
//     setOpenPDF(isOpen);
//   };

//   useEffect(() => {
//     const getData = async () => {
//       setLoading(true);
//       let total = 0;
//       const scores: ScoreResultType[] = [];

//       if (quizAnswer.results && quizAnswer.results.length > 0) {
//         for (const answer of quizAnswer.results) {
//           const question: any = await findQuestion(answer.questionId);
//           const scoreData =
//           getScore(question, answer, answer.categoryCognitiveQuestionId);

//           if (
//             answer.categoryCognitiveQuestionId === 2 &&
//             answer.questionId === 4
//           ) {
//             continue;
//           }

//           total += scoreData?.score || 0;
//           scores.push(scoreData);
//         }
//       }

//       setTotalScore(total);
//       setDataScore(scores);
//       setLoading(false);
//     };

//     getData();
//   }, [quizAnswer]);

//   const initDate = new Date(quizAnswer.results[1]?.initDate);
//   const endDate = new Date(
//     quizAnswer.results[quizAnswer.results.length - 1]?.endDateTime,
//   );
//   const timeTotal =
//     initDate && endDate ? calculateTimeDifference(initDate, endDate) : "N/A";
//     const answersWithScores =  {...quizAnswer, dataScore}

//   return (
//     // !loading && (
//     <div>
//       <Modal
//         open={openModal}
//         aria-labelledby="modal-modal-title"
//         aria-describedby="modal-modal-description"
//         sx={{ transform: valueTransformIfRtl }}
//         // onBlur={handleClose}
//       >
//         <Box
//           sx={{
//             position: "absolute" as const,
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//             minWidth: "75%",
//             height: "95%",
//             bgcolor: "#FFF",
//             color: "#000",
//             boxShadow: 3,
//             p: 4,
//             borderRadius: 1,
//             overflow: "auto",
//           }}
//         >
//           <Box
//             sx={{
//               position: "sticky",
//               top: 0,
//               left: -5,
//               display: "flex",
//               justifyContent: "flex-end",
//               zIndex: 10,
//             }}
//           >
//             <IconButton
//               onClick={handleClose}
//               sx={{ color: "#000", backgroundColor: "#f2f2f2" }}
//             >
//               <Close />
//             </IconButton>
//           </Box>
//           <div style={styles.container}>
//             <h1 style={styles.title}>
//               {translate("screenApp.results.answer.title")}
//             </h1>
//             <p style={styles.totalScore}>
//               {translate("screenApp.results.answer.score.title")}: {totalScore}
//             </p>

//             {quizAnswer.results.some(
//               answer => answer.typeQuiz === "screening",
//             ) && (
//               <>
//                 <h2 style={styles.subtitle}>
//                   {translate("screenApp.results.answer.screening")}:
//                 </h2>
//                 {quizAnswer.results
//                   .filter(answer => answer.typeQuiz === "screening")
//                   .map(answer => (
//                     <DisplayAnswer
//                       key={answer.id}
//                       answer={answer}
//                       findQuestion={findQuestion}
//                     />
//                   ))}
//               </>
//             )}

//             {quizAnswer.results.some(
//               answer => answer.typeQuiz === "anamnese",
//             ) && (
//               <>
//                 <h2 style={styles.subtitle}>
//                   {translate("screenApp.results.answer.initials")}:
//                 </h2>
//                 {quizAnswer.results
//                   .filter(answer => answer.typeQuiz === "anamnese")
//                   .map(answer => (
//                     <DisplayAnswer
//                       key={answer.id}
//                       answer={answer}
//                       findQuestion={findQuestion}
//                     />
//                   ))}
//               </>
//             )}

//             {quizAnswer.results.some(answer => answer.typeQuiz === "quiz") && (
//               <>
//                 <h2 style={styles.subtitle}>
//                   {translate("screenApp.results.answer.main")}:
//                 </h2>
//                 {quizAnswer.results
//                   .filter(answer => answer.typeQuiz === "quiz")
//                   .map(answer => (
//                     <DisplayAnswer
//                       key={answer.id}
//                       answer={answer}
//                       pressable
//                       time={true}
//                       findQuestion={findQuestion}
//                     />
//                   ))}
//               </>
//             )}

//             <p style={styles.timetotal}>
//               {translate("screenApp.results.answer.total.time")}: {timeTotal}
//             </p>

//             {/* {quizAnswer.some(answer => answer.quizId === 1) && <CutoffPoints />} */}

//             {/* {selectedAnswer && (
//         <AnswerDetailModal
//           visible={modalVisible}
//           onClose={() => setModalVisible(false)}
//           answer={selectedAnswer}
//         />
//       )} */}

//             <div style={styles.bottomView}>
//               <button
//                 style={styles.button}
//                 onClick={() => {
//                   handleOpenPDF(true);
//                 }}
//               >
//                 {translate("screenApp.results.openPdf").toUpperCase()}
//               </button>
//             </div>
//           </div>
//         </Box>
//       </Modal>
//       <ModalResultPDF
//         openPDF={openPDF}
//         setOpenPDF={handleOpenPDF}
//         data={answersWithScores}
//         accountId={accountId}
//         account={accountSelected}
//         totalScore={totalScore}
//         // dataScore={dataScore}
//         // setData={setItemData}
//         // accountId={account?.id}
//         // itIsAnamnesis={itIsAnamnese}
//       />
//       ;
//     </div>
//   );
//   // );
// };

// type DisplayAnswerProps = {
//   answer: any;
//   pressable?: boolean;
//   time?: boolean;
//   findQuestion: (questionId: number) => Promise<ApiQuestionType>;
// };
// const DisplayAnswer = ({
//   answer,
//   pressable = false,
//   time = false,
//   findQuestion,
// }: DisplayAnswerProps) => {
//   const [question, setQuestion] = useState({} as ApiQuestionType);
//   const translate = useTranslation();

//   useEffect(() => {
//     const start = async () => {
//       const data = await findQuestion(answer.questionId);
//       setQuestion(data);
//     };
//     start();
//   }, []);

//   const timeDiff = calculateTimeDifference(
//     new Date(answer.initDate),
//     new Date(answer.endDateTime),
//   );

//   let dataScore: ScoreResultType;
//   if (question) {
//     dataScore = getScore(question, answer, answer.categoryCognitiveQuestionId);
//     console.log("dataScore", dataScore);
//   }

//   const renderStyledChip = (label: string) => (
//     <Chip
//       label={label}
//       variant="outlined"
//       sx={{
//         boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
//         fontSize: "1.2rem",
//         height: "38px",
//         minWidth: "120px",
//       }}
//     />
//   );

//   const renderChipScore = () => {
//     const score = dataScore?.score ?? 0;

//     if (answer.categoryCognitiveQuestionId === 2 && answer.questionId === 4) {
//       return renderStyledChip(
//         translate("screenApp.results.answer.does.not.score"),
//       );
//     }

//     if (score !== null && score >= 0) {
//       return renderStyledChip(`${translate("labels.score")}: ${score}`);
//     }

//     return renderStyledChip("Erro");
//   };

//   const renderChipIntrusion = () => {
//     const intrusions = dataScore?.intrusions ?? 0;

//     if (intrusions > 0) {
//       return (
//         <Chip
//           variant="outlined"
//           label={`${translate("labels.intrusions")}: ${intrusions}`}
//           sx={{
//             boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
//             fontSize: "1.2rem",
//             height: "38px",
//             minWidth: "120px",
//             marginLeft: "10px",
//           }}
//         />
//       );
//     }
//   };

//   return (
//     answer.endDateTime && (
//       <Card key={answer.id} style={styles.answerContainer}>
//         <CardHeader
//           title={`Pergunta: ${question?.quest}`}
//           // subheader={question?.category?.name}
//         />
//         <CardContent>
//           {answer.rightAnswer && (
//             <p style={styles.answer}>
//               {translate("screenApp_results_answer_true")}: {answer.rightAnswer}
//             </p>
//           )}
//           <p style={styles.answer}>
//             {translate("screens_quiz_labels_answer")}: {answer.answer}
//           </p>
//           <p style={styles.date}>
//             {translate("labels_home")}:{" "}
//             {new Date(answer.initDate).toLocaleString()}
//           </p>
//           <p style={styles.date}>
//             {translate("labels_end")}:{" "}
//             {new Date(answer.endDateTime).toLocaleString()}
//           </p>
//         </CardContent>
//         {pressable && (
//           <div
//             style={{
//               display: "flex",
//               justifyContent: "space-between",
//               alignItems: "center",
//               padding: "10px",
//             }}
//           >
//             <div style={{ display: "flex", alignItems: "center" }}>
//               {renderChipScore()}
//               {renderChipIntrusion()}
//               {time && <p style={styles.timeDifference}>{timeDiff}</p>}
//             </div>
//             <Button
//               variant="contained"
//               color="info"
//               sx={{ fontSize: "1.1rem" }}
//               // onClick={() => handleAnswerPress(answer)}
//             >
//               {translate("screenApp_listPacient_buttonsPacient_buttonDetail")}
//             </Button>
//           </div>
//         )}
//       </Card>
//     )
//   );
// };

// const styles: Record<string, React.CSSProperties> = {
//   container: {
//     display: "flex",
//     flexDirection: "column",
//     padding: "20px",
//   },
//   title: {
//     fontSize: "34px",
//     marginBottom: "16px",
//     textAlign: "center",
//   },
//   totalScore: {
//     fontSize: "20px",
//     marginBottom: "16px",
//     textAlign: "center",
//     fontWeight: "bold",
//     borderWidth: "1px",
//     borderStyle: "solid",
//     borderColor: "#ccc",
//     padding: "4px 10px",
//     borderRadius: "5px",
//   },
//   subtitle: {
//     fontSize: "20px",
//     margin: "16px 0",
//   },
//   timetotal: {
//     fontSize: "20px",
//     margin: "16px 0",
//     textAlign: "right",
//   },
//   answerContainer: {
//     marginBottom: "20px",
//     borderRadius: "8px",
//     paddingBottom: "8px",
//     boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
//     backgroundColor: "#f2f2f2",
//   },
//   answerTextContainer: {
//     flex: 1,
//   },
//   question: {
//     fontSize: "18px",
//     fontWeight: "bold",
//   },
//   answer: {
//     fontSize: "16px",
//     margin: "5px 0",
//     fontWeight: "bold",
//   },
//   date: {
//     fontSize: "14px",
//     color: "gray",
//   },
//   timeDifference: {
//     fontSize: "14px",
//     fontWeight: "bold",
//     textAlign: "left",
//     alignSelf: "center",
//   },
//   button: {
//     backgroundColor: "#007bff",
//     color: "#fff",
//     padding: "10px 20px",
//     borderRadius: "8px",
//     fontSize: "16px",
//     fontWeight: "bold",
//     border: "none",
//     cursor: "pointer",
//     boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
//     transition: "all 0.3s ease",
//     outline: "none",
//   },
//   bottomView: {
//     display: "flex",
//     flexDirection: "row",
//     margin: "1px",
//     marginInline: "18px",
//     marginBottom: "18px",
//     justifyContent: "center",
//     alignItems: "center",
//   },
// };

// export default ModalCognitiveDetails;
